import React, { useEffect, useState, useImperativeHandle, forwardRef, } from 'react';
import Button from '../Button/Button';
import './AnalyseOffreTechnique.css';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';


// Types et interfaces
interface Presta {
  id: string;
  presta_id: number;
  id_status: number;
  nom: string;
  organisme: string;
  offreTechnique: {
    reponseTechnique: string;
  };
  dateReponse: string;
}

interface CritereNote {
  note?: number;
  commentaire?: string;
  description?: string;
}

interface TechNotes {
  [prestaId: string]: {
    [critere: string]: CritereNote;
  };
}

interface CritereOption {
  value: number;
  label: string;
}

interface Critere {
  key: string;
  label: string;
  maxPoints: number;
  options: CritereOption[];
  description: string;
}

interface AnalyseOffreTechniqueProps {
  prestataires: Presta[];
  techNotes: TechNotes;
  setTechNotes: React.Dispatch<React.SetStateAction<TechNotes>>;
  onNext: () => void;
  viewOnly?: boolean;
  generatePrestatairePDF?: (prestaId: number) => Promise<void>; // Nouvelle prop
  operation: any;

}

// Constantes
const CRITERES: Critere[] = [
  {
    key: 'organisation',
    label: 'Modes Opératoires pour l\'éxecution du marché',
    maxPoints: 5,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1.25, label: '1,25 - Pas satisfaisant' },
      { value: 2.5, label: '2,50 - Peu satisfaisant' },
      { value: 3.75, label: '3,75 - Assez satisfaisant' },
      { value: 5, label: '5,00 - Satisfaisant' },
    ],
    description: "Évaluation des méthodologies, processus et approches proposés par le prestataire pour l'exécution du marché. Cette section examine la cohérence et l'efficacité des méthodes de travail, l'organisation du chantier, la gestion des interfaces et la qualité de l'encadrement."
  },
  {
    key: 'moyens',
    label: 'Moyens Humains et Materiels dédiés au marché',
    maxPoints: 4,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1, label: '1,00 - Pas satisfaisant' },
      { value: 2, label: '2,00 - Peu satisfaisant' },
      { value: 3, label: '3,00 - Assez satisfaisant' },
      { value: 4, label: '4,00 - Satisfaisant' },
    ],
    description: "Évaluation des ressources humaines (équipes, compétences, qualifications) et matérielles (équipements, outils, logiciels) que le prestataire propose de mobiliser pour l'exécution du marché. Cette section examine l'adéquation des moyens avec les exigences techniques et le calendrier du projet."
  },
  {
    key: 'social',
    label: 'Mesures en matière Environnementale & Sociale',
    maxPoints: 1,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 0.25, label: '0,25 - Pas satisfaisant' },
      { value: 0.5, label: '0,50 - Peu satisfaisant' },
      { value: 0.75, label: '0,75 - Assez satisfaisant' },
      { value: 1, label: '1,00 - Satisfaisant' },
    ],
    description: "Évaluation des engagements et dispositifs proposés par le prestataire en matière de responsabilité environnementale (gestion des déchets, réduction des nuisances, économies d'énergie) et sociale (insertion, conditions de travail, sous-traitance responsable)."
  }
];

/**
 * Composant pour l'analyse des offres techniques des prestataires
 */
const AnalyseOffreTechnique = forwardRef<{
  validate: () => boolean;
}, AnalyseOffreTechniqueProps>(({
  prestataires,
  techNotes,
  setTechNotes,
  onNext,
  viewOnly = false,
  generatePrestatairePDF,
  operation
}, ref) => {
  // États
  const [criteresInfo, setCriteresInfo] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [globalError, setGlobalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const getCandidatureDate = (commandeId: string) => {
    if (!operation?.historique) {
      console.warn('Operation historique is missing');
      return null;
    }

    const entry = operation.historique.find(
      (h: any) =>
        h.prefix === 'Candidature' &&
        h.destinataire?.toString() === commandeId?.toString()
    );

    if (!entry) {
      console.warn(`No candidature entry found for commande ${commandeId}`);
      return null;
    }

    return entry.created_at;
  };

  // Effets
  useEffect(() => {
    // Chargement des informations des critères
    const loadCriteresInfo = async () => {
      try {
        // Ici on pourrait appeler une API réelle
        // Pour l'instant, on initialise avec des objets vides
        setCriteresInfo({
          organisation: "",
          moyens: "",
          social: ""
        });
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement des critères:", error);
        setLoading(false);
      }
    };

    loadCriteresInfo();
  }, []);

  // Fonctions utilitaires
  /**
   * Calcule la note globale d'un prestataire sur 10
   */
  const getNoteGlobale = (prestaId: string): string => {
    let totalPoints = 0;
    let maxPossiblePoints = 0;

    CRITERES.forEach(critere => {
      const note = techNotes[prestaId]?.[critere.key]?.note || 0;
      totalPoints += note;
      maxPossiblePoints += critere.maxPoints;
    });

    return maxPossiblePoints > 0 ?
      ((totalPoints / maxPossiblePoints) * 10).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :
      "0,00";
  };

  /**
   * Valide les notes et commentaires des prestataires
   */
  const validate = (): boolean => {
    let hasError = false;
    const newErrors: Record<string, any> = {};
    let firstErrorElementId: string | null = null;

    prestataires.forEach((presta) => {
      // Ne pas valider les offres expirées
      const isExpired = presta.id_status === 12;
      const isRefused = presta.id_status === 6;
      if (isExpired || isRefused || (presta.id_status >= 7 && presta.id_status <= 11)) {
        return; // Ignorer complètement ce prestataire dans la validation
      }

      newErrors[presta.id] = {};

      CRITERES.forEach(({ key }) => {
        const note = techNotes[presta.id]?.[key]?.note;
        const commentaire = techNotes[presta.id]?.[key]?.commentaire;
        newErrors[presta.id][key] = {};

        if (note === undefined || isNaN(note)) {
          newErrors[presta.id][key].note = 'La note est requise';
          hasError = true;

          if (!firstErrorElementId) {
            firstErrorElementId = `${presta.id}-${key}-note`;
          }
        }

        if (!commentaire || commentaire.trim() === '') {
          newErrors[presta.id][key].commentaire = 'Le commentaire est requis';
          hasError = true;

          if (!firstErrorElementId) {
            firstErrorElementId = `${presta.id}-${key}-commentaire`;
          }
        }
      });
    });

    setErrors(newErrors);

    if (hasError) {
      setGlobalError('Merci de remplir tous les champs obligatoires.');
      if (firstErrorElementId) {
        const elementId = firstErrorElementId; // Copier la valeur pour éviter les problèmes de type
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
    }

    return !hasError;
  };

  useImperativeHandle(ref, () => ({
    validate
  }));

  // Gestionnaires d'événements
  /**
   * Gère la mise à jour des notes ou commentaires
   */
  const handleChange = (prestaId: string, critere: string, field: string, value: any) => {
    setTechNotes((prev) => ({
      ...prev,
      [prestaId]: {
        ...prev[prestaId],
        [critere]: {
          ...((prev[prestaId] && prev[prestaId][critere]) || {}),
          [field]: value
        }
      }
    }));

    // Réinitialise les erreurs pour ce champ
    setErrors((prev) => ({
      ...prev,
      [prestaId]: {
        ...prev[prestaId],
        [critere]: {
          ...((prev[prestaId] && prev[prestaId][critere]) || {}),
          [field]: ''
        }
      }
    }));

    setGlobalError('');
  };

  /**
   * Gère le changement de note
   */
  const handleNoteChange = (prestaId: string, critere: string, value: string) => {
    handleChange(prestaId, critere, 'note', value === '' ? '' : Number(value));
  };

  /**
   * Gère le changement de commentaire
   */
  const handleCommentChange = (prestaId: string, critere: string, value: string) => {
    handleChange(prestaId, critere, 'commentaire', value);
  };

  /**
   * Gère la navigation vers l'étape suivante
   */
  const handleNextClick = () => {
    onNext();
  };

  /**
   * Sauvegarde les données en brouillon
   */
  const handleSaveDraft = async () => {
    setIsSaving(true);
    setSuccessMsg('');

    try {
      const response = await fetch('/api/marche-subsequent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'Brouillon',
          techNotes: techNotes
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      setSuccessMsg('Votre brouillon a bien été sauvegardé.');
    } catch (error) {
      setSuccessMsg("Erreur lors de la sauvegarde du brouillon.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrestatairePDF = (prestaId: string) => {
    if (generatePrestatairePDF) {
      const numericId = parseInt(prestaId, 10);
      if (!isNaN(numericId)) {
        generatePrestatairePDF(numericId);
      }
    }
  };

  // Affiche un indicateur de chargement si les données ne sont pas prêtes
  if (loading) {
    return <div className="loader-container">Chargement des critères...</div>;
  }

  // Vérifier si des prestataires sont disponibles
  if (!prestataires || prestataires.length === 0) {
    return <div className="no-data">Aucune offre technique disponible pour cette opération.</div>;
  }
  console.log(techNotes)



  // Rendu du composant
  return (
    <div className="step-container">
      <p className='analyse-info'>
        Une IA d’assistance à l’analyse des offres est en cours de déploiement.
        Dans cette attente, il est chargé par défaut les notes techniques et commentaires que le candidat a obtenu pour l’accord-cadre en vigueur.
      </p>
      {/* Message d'erreur global */}
      {globalError && (
        <div className="error-message validation-error" style={{ marginBottom: '2rem' }}>
          {globalError}
        </div>
      )}

      {/* Message de succès */}
      {successMsg && (
        <div className="success-message" style={{ marginBottom: '2rem' }}>
          {successMsg}
        </div>
      )}

      {/* Grille de comparaison des prestataires */}
      <div className="presta-comparaison-grid">
        {prestataires.map((presta, index) => {
          const prestaKey = presta.id; // <-- correspond à la clé utilisée dans techNotes
          const isExpired = presta.id_status === 12;
          const isRefused = presta.id_status === 6;
          const candidatureDate = getCandidatureDate(presta.presta_id.toString());
          const isAccepted = presta.id_status === 11;
          const isPending = [7, 8, 9, 10].includes(presta.id_status);

          return (

            <div className={`presta-col ${(isExpired || isRefused) ? "disabled" : ""}${isPending || isAccepted ? "accepted" : ""}`} key={presta.id}>

              {isExpired && <div className="expired-badge">Expirée</div>}
              {isRefused && <div className="expired-badge">Refusée</div>}
              {!isExpired && !isRefused && isPending && (
                <div className="best-note-badge">Signature en cours</div>
              )}
              {!isExpired && !isRefused && isAccepted && (
                <div className="best-note-badge">Acceptée</div>
              )}

              <div className="presta-conten">
                <div className="presta-header">
                  <h3>{presta.organisme}</h3>
                  {isExpired ? (
                    <span>Offre non reçue</span>
                  ) : (
                    <span>
                      Offre reçue le {candidatureDate
                        ? `${new Date(candidatureDate).toLocaleDateString('fr-FR')} à ${new Date(candidatureDate).getHours().toString().padStart(2, '0')}h${new Date(candidatureDate).getMinutes().toString().padStart(2, '0')}`
                        : '-'}
                    </span>
                  )}
                </div>

                <div className="presta-footer-right pdf-button">
                  {generatePrestatairePDF && (
                    <Button
                      value="PDF"
                      icon={<PdfIcon />}
                      variant="secondary"
                      size="small"
                      onClick={() => handlePrestatairePDF(presta.id)}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Réponse technique au besoin :</label>
                  <div className="text-other-user">
                    {isExpired ? "OFFRE EXPIRÉE" : (presta.offreTechnique?.reponseTechnique || "Aucune réponse technique fournie.")}
                  </div>
                </div>

                <hr className='darkblue' />

                {CRITERES.map(({ key, label, description, maxPoints, options }) => (
                  <div key={key} className="form-group">
                    <span className="label">{label}</span>
                    <div className="text-other-user">
                      {isExpired ? "OFFRE EXPIRÉE" : (techNotes[prestaKey]?.[key]?.description || description || "Aucune réponse technique fournie.")}
                    </div>

                    <label>
                      Votre Commentaire<span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                      id={`${presta.id}-${key}-commentaire`}
                      placeholder={isExpired ? "Commentaire non requis pour offre expirée" : "Votre commentaire"}
                      value={isExpired ? "OFFRE EXPIRÉE - Commentaire non requis" : (techNotes[prestaKey]?.[key]?.commentaire || '')}
                      onChange={e => handleCommentChange(prestaKey, key, e.target.value)}
                      className={`input${errors[prestaKey]?.[key]?.commentaire ? ' error' : ''}`}
                      disabled={viewOnly || isExpired || isRefused || (presta.id_status >= 7 && presta.id_status <= 11)}
                    />

                    <div className="note-select-row">
                      <label>Note <span style={{ color: 'red' }}>*</span> :</label>
                      <select
                        id={`${presta.id}-${key}-note`}
                        value={isExpired ? 0 : (techNotes[prestaKey]?.[key]?.note ?? options[0].value)}
                        onChange={e => handleNoteChange(prestaKey, key, e.target.value)}
                        className={`input note-select${errors[prestaKey]?.[key]?.note ? ' error' : ''}`}
                        disabled={viewOnly || isExpired}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <span className="note-out-of">/{maxPoints}</span>
                    </div>
                  </div>
                ))}
              </div>



              <div className="presta-footer">
                <div className="presta-footer-left">
                  <div className="note-globale">
                    <h4>Note technique </h4>
                    <div className="note-value">
                      {isExpired ? "-" : `${getNoteGlobale(prestaKey)} / 10`}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
});

export default AnalyseOffreTechnique;